import React from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import { ADD_TRAINER_DETAILS } from "AttendanceForm/Utils/Queries/TrainerDetails";
import { useFormSubmission } from "AttendanceForm/Utils/Forms/useFormSubmission";

export interface AddTrainerDetailsFormValues {
  firstName: string;
  lastName: string;
  email: string;
  eventId: string;
  eventHash: string;
}

const defaultValues: AddTrainerDetailsFormValues = {
  firstName: "",
  lastName: "",
  email: "",
  eventId: "",
  eventHash: "",
};

interface UseAddTrainerDetailsFormProps {
  initialValues?: Partial<AddTrainerDetailsFormValues>;
  eventId?: string;
  eventHash?: string;
}

export const useAddTrainerDetailsForm = ({
  initialValues,
  eventId,
  eventHash,
}: UseAddTrainerDetailsFormProps = {}) => {
  const validationSchema = useAddTrainerDetailsSchema();

  const form = useFormik<AddTrainerDetailsFormValues>({
    validationSchema,
    initialValues: {
      ...defaultValues,
      ...initialValues,
    },
    validateOnChange: true,
    validateOnBlur: false,
    onSubmit: () => {},
  });
  const { loading, onSubmit } = useFormSubmission({
    form: form,
    map: (values: AddTrainerDetailsFormValues) => ({
      trainerDetails: {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        eventId: eventId,
        eventHash: eventHash,
      },
    }),
    query: ADD_TRAINER_DETAILS,
  });

  return {
    form,
    onSubmit,
    loading,
  };
};

const useAddTrainerDetailsSchema = () => {
  const { t } = useTranslation("trainerDetailsForm");
  return React.useMemo(
    () =>
      yup.object().shape({
        firstName: yup.string().required(t("firstnameRequired")),
        lastName: yup.string().required(t("lastnameRequired")),
        email: yup
          .string()
          .matches(
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
            t("emailInvalid")
          )
          .required(t("emailRequired")),
      }),
    [t]
  );
};
